import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Loader2, Upload, Trash2, Plus, ChevronDown, ChevronRight, ShieldCheck, LayoutDashboard, BarChart3 } from "lucide-react";
import { toast, Toaster } from "sonner";
import { api, imgUrl, adminHeaders } from "../lib/api";
import { Logo } from "../components/Logo";
import { AdminAnalytics } from "../components/AdminAnalytics";

const field = "w-full bg-[#111] border border-white/10 rounded-lg px-3 py-2.5 text-sm text-white outline-none focus:ring-1 focus:ring-blood";
const btn = "px-4 py-2 rounded-full bg-blood text-white text-sm font-bold hover:bg-blood-hover transition-colors disabled:opacity-40";

function ImageUpload({ label, value, onChange, testId }) {
  const [uploading, setUploading] = useState(false);
  const upload = async (file) => {
    if (!file) return;
    setUploading(true);
    const fd = new FormData();
    fd.append("file", file);
    try {
      const r = await api.post("/upload", fd, { headers: { ...adminHeaders(), "Content-Type": "multipart/form-data" } });
      onChange(r.data.url);
      toast.success(`${label} uploaded`);
    } catch (e) { toast.error("Upload failed"); }
    setUploading(false);
  };
  return (
    <div>
      <label className="text-xs text-white/50 mb-1 block">{label}</label>
      <div className="flex items-center gap-3">
        {value && <img src={imgUrl(value)} alt="" className="w-14 h-14 object-cover rounded-md border border-white/10" />}
        <label className="flex items-center gap-2 px-3 py-2 rounded-lg bg-white/5 border border-white/10 text-xs text-white/70 cursor-pointer hover:bg-white/10">
          {uploading ? <Loader2 size={14} className="animate-spin" /> : <Upload size={14} />} Upload
          <input type="file" accept="image/*" hidden data-testid={testId} onChange={(e) => upload(e.target.files[0])} />
        </label>
        <input value={value || ""} onChange={(e) => onChange(e.target.value)} placeholder="or paste URL" className={field + " flex-1"} />
      </div>
    </div>
  );
}

function EpisodeForm({ seriesId, onDone }) {
  const [f, setF] = useState({ season_number: 1, episode_number: 1, title: "", description: "", duration: "", thumbnail_url: "", video_url: "" });
  const [saving, setSaving] = useState(false);
  const save = async () => {
    if (!f.title) return toast.error("Episode title required");
    setSaving(true);
    try {
      await api.post("/episodes", { ...f, series_id: seriesId, season_number: Number(f.season_number), episode_number: Number(f.episode_number) }, { headers: adminHeaders() });
      toast.success("Episode added");
      setF({ season_number: f.season_number, episode_number: Number(f.episode_number) + 1, title: "", description: "", duration: "", thumbnail_url: "", video_url: "" });
      onDone();
    } catch (e) { toast.error("Failed to add episode"); }
    setSaving(false);
  };
  return (
    <div className="bg-black/40 border border-white/10 rounded-lg p-4 mt-3 space-y-3" data-testid="episode-form">
      <div className="grid grid-cols-3 gap-3">
        <div><label className="text-xs text-white/50">Season</label><input type="number" value={f.season_number} onChange={(e) => setF({ ...f, season_number: e.target.value })} className={field} data-testid="ep-season" /></div>
        <div><label className="text-xs text-white/50">Episode</label><input type="number" value={f.episode_number} onChange={(e) => setF({ ...f, episode_number: e.target.value })} className={field} data-testid="ep-number" /></div>
        <div><label className="text-xs text-white/50">Duration</label><input value={f.duration} onChange={(e) => setF({ ...f, duration: e.target.value })} placeholder="24 min" className={field} /></div>
      </div>
      <input value={f.title} onChange={(e) => setF({ ...f, title: e.target.value })} placeholder="Episode title" className={field} data-testid="ep-title" />
      <textarea value={f.description} onChange={(e) => setF({ ...f, description: e.target.value })} placeholder="Episode description" rows={2} className={field} />
      <input value={f.video_url} onChange={(e) => setF({ ...f, video_url: e.target.value })} placeholder="Video URL (mp4/hls)" className={field} data-testid="ep-video-url" />
      <ImageUpload label="Thumbnail" value={f.thumbnail_url} onChange={(v) => setF({ ...f, thumbnail_url: v })} testId="ep-thumb-upload" />
      <button onClick={save} disabled={saving} className={btn} data-testid="ep-save-btn">{saving ? "Saving..." : "Add Episode"}</button>
    </div>
  );
}

function SeriesRow({ s, refresh }) {
  const [open, setOpen] = useState(false);
  const [detail, setDetail] = useState(null);
  const load = () => api.get(`/series/${s.id}`).then((r) => setDetail(r.data));
  useEffect(() => { if (open) load(); }, [open]);
  const del = async () => {
    if (!window.confirm(`Delete "${s.title}" and all episodes?`)) return;
    await api.delete(`/series/${s.id}`, { headers: adminHeaders() });
    toast.success("Series deleted");
    refresh();
  };
  const delEp = async (eid) => {
    await api.delete(`/episodes/${eid}`, { headers: adminHeaders() });
    toast.success("Episode deleted");
    load();
  };
  return (
    <div className="bg-[#0A0A0A] border border-white/10 rounded-xl overflow-hidden" data-testid={`admin-series-${s.id}`}>
      <div className="flex items-center gap-3 p-3">
        <button onClick={() => setOpen(!open)} className="text-white/60">{open ? <ChevronDown size={18} /> : <ChevronRight size={18} />}</button>
        {s.poster_url ? <img src={imgUrl(s.poster_url)} alt="" className="w-10 h-14 object-cover rounded" /> : <div className="w-10 h-14 bg-white/5 rounded" />}
        <div className="flex-1 min-w-0">
          <p className="font-semibold text-white truncate">{s.title} {s.featured && <span className="text-[10px] text-blood ml-1">★ Featured</span>}</p>
          <p className="text-xs text-white/40 truncate">{s.genres?.join(", ")}</p>
        </div>
        <button onClick={del} data-testid={`delete-series-${s.id}`} className="text-white/40 hover:text-blood p-2"><Trash2 size={18} /></button>
      </div>
      {open && (
        <div className="border-t border-white/10 p-4">
          <p className="text-xs uppercase tracking-widest text-white/40 mb-2">Episodes</p>
          {!detail ? <Loader2 className="animate-spin text-blood" size={18} /> : detail.episodes?.length === 0 ? <p className="text-white/40 text-sm">No episodes.</p> : (
            <div className="space-y-2">
              {detail.episodes.map((e) => (
                <div key={e.id} className="flex items-center gap-2 text-sm bg-black/40 rounded-lg px-3 py-2">
                  <span className="text-blood font-bold text-xs">S{e.season_number}E{e.episode_number}</span>
                  <span className="text-white flex-1 truncate">{e.title}</span>
                  {!e.video_url && <span className="text-[10px] text-yellow-500">no video</span>}
                  <button onClick={() => delEp(e.id)} className="text-white/30 hover:text-blood"><Trash2 size={14} /></button>
                </div>
              ))}
            </div>
          )}
          <EpisodeForm seriesId={s.id} onDone={load} />
        </div>
      )}
    </div>
  );
}

export default function Admin() {
  const [authed, setAuthed] = useState(!!localStorage.getItem("dakid_admin_token"));
  const [email, setEmail] = useState("");
  const [pw, setPw] = useState("");
  const [loggingIn, setLoggingIn] = useState(false);
  const [series, setSeries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [form, setForm] = useState({ title: "", description: "", genres: "", year: "", poster_url: "", banner_url: "", featured: false });
  const [saving, setSaving] = useState(false);
  const [tab, setTab] = useState("content");

  const refresh = () => { setLoading(true); api.get("/series").then((r) => setSeries(r.data)).finally(() => setLoading(false)); };

  useEffect(() => {
    if (!authed) return;
    api.get("/auth/me", { headers: adminHeaders() })
      .then(() => refresh())
      .catch(() => { localStorage.removeItem("dakid_admin_token"); setAuthed(false); });
  }, [authed]);

  const logout = () => { localStorage.removeItem("dakid_admin_token"); setAuthed(false); };

  const login = async () => {
    if (!email || !pw) return;
    setLoggingIn(true);
    try {
      const r = await api.post("/auth/login", { email, password: pw });
      localStorage.setItem("dakid_admin_token", r.data.token);
      setAuthed(true);
      setPw("");
      toast.success("Welcome back");
    } catch (e) {
      toast.error(e.response?.status === 429 ? "Too many attempts. Try later." : "Invalid email or password");
    }
    setLoggingIn(false);
  };

  const createSeries = async () => {
    if (!form.title) return toast.error("Title required");
    setSaving(true);
    try {
      await api.post("/series", {
        ...form,
        genres: form.genres.split(",").map((g) => g.trim()).filter(Boolean),
      }, { headers: adminHeaders() });
      toast.success("Series created");
      setForm({ title: "", description: "", genres: "", year: "", poster_url: "", banner_url: "", featured: false });
      refresh();
    } catch (e) {
      if (e.response?.status === 401) { logout(); toast.error("Session expired, please log in"); }
      else toast.error("Failed to create series");
    }
    setSaving(false);
  };

  if (!authed)
    return (
      <div className="min-h-screen grain flex items-center justify-center px-6">
        <Toaster theme="dark" position="top-center" />
        <div className="w-full max-w-sm bg-[#0A0A0A] border border-white/10 rounded-2xl p-8 text-center">
          <Logo size="text-3xl" className="mb-2 justify-center flex" />
          <p className="text-white/50 text-sm mb-6 flex items-center gap-2 justify-center"><ShieldCheck size={16} /> Admin Sign In</p>
          <input type="email" value={email} onChange={(e) => setEmail(e.target.value)} onKeyDown={(e) => e.key === "Enter" && login()} placeholder="Email" className={field + " mb-3"} data-testid="admin-email-input" />
          <input type="password" value={pw} onChange={(e) => setPw(e.target.value)} onKeyDown={(e) => e.key === "Enter" && login()} placeholder="Password" className={field + " mb-4"} data-testid="admin-password-input" />
          <button onClick={login} disabled={loggingIn} className={btn + " w-full"} data-testid="admin-login-btn">{loggingIn ? "Signing in..." : "Sign In"}</button>
        </div>
      </div>
    );

  return (
    <div className="min-h-screen grain pb-16">
      <Toaster theme="dark" position="top-center" />
      <div className="border-b border-red-900/30 bg-black/60 backdrop-blur-xl sticky top-0 z-40">
        <div className="max-w-[1100px] mx-auto px-4 sm:px-8 h-16 flex items-center justify-between">
          <Logo size="text-xl" />
          <div className="flex items-center gap-1 sm:gap-2">
            <button onClick={() => setTab("content")} data-testid="tab-content" className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full text-sm font-semibold transition-colors ${tab === "content" ? "bg-blood/20 text-white glow-box" : "text-white/50 hover:text-white"}`}><LayoutDashboard size={16} /> <span className="hidden sm:inline">Content</span></button>
            <button onClick={() => setTab("analytics")} data-testid="tab-analytics" className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full text-sm font-semibold transition-colors ${tab === "analytics" ? "bg-blood/20 text-white glow-box" : "text-white/50 hover:text-white"}`}><BarChart3 size={16} /> <span className="hidden sm:inline">Analytics</span></button>
            <Link to="/" className="text-sm text-white/60 hover:text-white ml-2 hidden sm:inline">View Site →</Link>
            <button onClick={logout} className="text-sm text-white/40 hover:text-blood ml-1">Lock</button>
          </div>
        </div>
      </div>

      {tab === "analytics" ? (
        <div className="max-w-[1100px] mx-auto px-4 sm:px-8 pt-10">
          <AdminAnalytics />
        </div>
      ) : (
      <div className="max-w-[1100px] mx-auto px-4 sm:px-8 pt-10 grid lg:grid-cols-2 gap-8">
        {/* Create */}
        <div>
          <h2 className="font-display font-bold text-2xl text-white mb-4 flex items-center gap-2"><Plus size={22} className="text-blood" /> New Series</h2>
          <div className="space-y-3 bg-[#0A0A0A] border border-white/10 rounded-xl p-5">
            <input value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} placeholder="Series title" className={field} data-testid="series-title-input" />
            <textarea value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} placeholder="Description" rows={3} className={field} data-testid="series-desc-input" />
            <div className="grid grid-cols-2 gap-3">
              <input value={form.genres} onChange={(e) => setForm({ ...form, genres: e.target.value })} placeholder="Genres (comma separated)" className={field} data-testid="series-genres-input" />
              <input value={form.year} onChange={(e) => setForm({ ...form, year: e.target.value })} placeholder="Year" className={field} />
            </div>
            <ImageUpload label="Poster (2:3)" value={form.poster_url} onChange={(v) => setForm({ ...form, poster_url: v })} testId="series-poster-upload" />
            <ImageUpload label="Banner (16:9)" value={form.banner_url} onChange={(v) => setForm({ ...form, banner_url: v })} testId="series-banner-upload" />
            <label className="flex items-center gap-2 text-sm text-white/70 cursor-pointer">
              <input type="checkbox" checked={form.featured} onChange={(e) => setForm({ ...form, featured: e.target.checked })} data-testid="series-featured-checkbox" /> Featured on home
            </label>
            <button onClick={createSeries} disabled={saving} className={btn} data-testid="create-series-btn">{saving ? "Creating..." : "Create Series"}</button>
          </div>
        </div>

        {/* Manage */}
        <div>
          <h2 className="font-display font-bold text-2xl text-white mb-4">Manage Series</h2>
          {loading ? <Loader2 className="animate-spin text-blood" /> : series.length === 0 ? <p className="text-white/40">No series yet.</p> : (
            <div className="space-y-3">{series.map((s) => <SeriesRow key={s.id} s={s} refresh={refresh} />)}</div>
          )}
        </div>
      </div>
      )}
    </div>
  );
}
